import { decode } from 'light-bolt11-decoder';

/**
 * Parses a BOLT11 invoice and returns the amount in sats.
 *
 * Resilient by design:
 * 1. Prefers light-bolt11-decoder; accepts its amount value as a number
 *    (assumed msats) or a string with msat/sat suffixes.
 * 2. Falls back to parsing the human-readable part (HRP), e.g.
 *    `lnbc2100n1...` → 210 sats, so totals survive decoder shape changes.
 * 3. Never throws. Returns 0 only when the invoice is truly unparseable.
 */
export function bolt11ToSats(bolt11: string): number {
  // 1. Decoder path
  try {
    const decoded = decode(bolt11);
    const amountSection = decoded.sections.find((s) => s.name === 'amount');
    const value = amountSection?.value;

    if (typeof value === 'number' && Number.isFinite(value)) {
      // decoder returns millisatoshis as a number
      return Math.max(0, Math.round(value / 1000));
    }

    if (typeof value === 'string') {
      const sats = satsFromAmountString(value);
      if (sats !== null) return sats;
    }
  } catch {
    // fall through to HRP parsing
  }

  // 2. HRP fallback: lnbc<amount><multiplier>1...
  return satsFromHrp(bolt11);
}

/** Parses "1234msat", "1234msats", "12sat", "12sats", "12msatoshi" → sats. */
function satsFromAmountString(raw: string): number | null {
  const s = raw.trim().toLowerCase();
  if (!s) return null;

  if (/^[0-9]+msats?$/.test(s)) {
    const num = parseInt(s.replace(/[^0-9]/g, ''), 10);
    return Number.isFinite(num) ? Math.round(num / 1000) : null;
  }
  if (/^msatoshi[0-9]+$/.test(s)) {
    const num = parseInt(s.replace(/[^0-9]/g, ''), 10);
    return Number.isFinite(num) ? Math.round(num / 1000) : null;
  }
  if (/^[0-9]+sats?$/.test(s)) {
    const num = parseInt(s.replace(/[^0-9]/g, ''), 10);
    return Number.isFinite(num) ? num : null;
  }
  if (/^[0-9]+$/.test(s)) {
    // plain numeric string: assume millisats (decoder convention)
    const num = parseInt(s, 10);
    return Number.isFinite(num) ? Math.round(num / 1000) : null;
  }
  return null;
}

/** Parses the invoice prefix `lnbc<amount><multiplier>` → sats. */
function satsFromHrp(bolt11: string): number {
  const match = /^lnbc([0-9]+)([munp]?)1/i.exec(bolt11.trim());
  if (!match) return 0; // no-amount invoice or malformed

  const amount = parseInt(match[1], 10);
  if (!Number.isFinite(amount)) return 0;

  const multiplier: Record<string, number> = {
    '': 1e8, // whole BTC
    m: 1e5, // milli   → sats
    u: 1e2, // micro   → sats
    n: 0.1, // nano    → sats
    p: 1e-4, // pico   → sats
  };

  const sats = amount * multiplier[match[2].toLowerCase()];
  return Math.round(sats);
}

/**
 * Classifies a zap receipt (kind 9735) against the expected recipient.
 *
 * - `verified`   : `p` tag exists and equals the expected pubkey.
 * - `unverified` : `p` tag missing (legacy receipts) — counted separately,
 *                  displayed with a badge, never used for ranking.
 * - `spoof`      : `p` names a different pubkey (self-pay spoofing) — dropped.
 *
 * Split-tolerant per NIP-57 Appendix G: receipts whose `p` matches the
 * author count fully; only mismatched recipients are rejected.
 */
export type ZapAttribution = 'verified' | 'unverified' | 'spoof';

export function classifyZapReceipt(
  receiptTags: string[][],
  expectedPubkey: string | undefined,
): ZapAttribution {
  if (!expectedPubkey) return 'spoof';
  const p = receiptTags.find(([t]) => t === 'p')?.[1];
  if (!p) return 'unverified';
  return p === expectedPubkey ? 'verified' : 'spoof';
}
